import React, { useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import moment from 'moment';
import { Api, Notification } from 'services';
import Broadcast from './index';
import PreJoin from './localComponent/preJoin';
import RecordingModal from './localComponent/recordingModal';
import ChatClient from './chatClient';
import ChatPanel from './localComponent/chatPanel';
import MessageNotification from './localComponent/messageNotification';
import { roleOf } from './localComponent/participantsPanel';
import { v4 as uuidv4 } from 'uuid';
import {
  getVideoResourceSections,
  addMessageToGroup,
  addMessageToPrivate,
  postChat,
  postChatImage,
  updateUnreadCount,
  postMessageOpenStatus,
  setCurrentPrivateChatReciever,
} from 'actions/broadcast';
import { postCreateTopic, getStorageDetails } from 'actions/digitalLibrary';
import { leaveBroadcast } from 'actions/schedule';
import { setUpChatSocket } from './utils';
import AulasSubscriptionModal from 'pages/common/aulasSubsriptionModal';

// Students share the /broadcast route with teachers. Set to false to send students
// straight into the room the way they were before the redesign.
const GATE_STUDENTS = true;

// Three publish heartbeats (2s each) of silence before a publisher is treated as gone. Two
// would call a single dropped message the end of someone's class.
const PUBLISHER_STALE_MS = 6000;

// How long to wait for a room that does not answer.
//
// This screen has one question on arrival — is somebody already publishing — because the
// answer decides whether this teacher is walked straight into that class or shown the
// pre-join screen. Nothing on the way in answers it: join-class returns the roster and the
// chat history, not who is on the wire, and the lobby socket only listens.
//
// Waiting out a heartbeat period was the first answer and the wrong one: 2s of staring at a
// spinner, charged to every teacher, overwhelmingly to the one about to start a class who
// had nothing to wait for. So the room is asked instead. A probe goes out the moment the
// socket is up and everyone who could publish answers at once — see the probe branch in
// receiveChatRef — which closes the question in a round trip rather than in a heartbeat
// period, whichever way it closes.
//
// This is only the backstop, for a room with a teacher in it who never answers: an older
// build that does not know the probe, or a tab that has stopped servicing its socket. Long
// enough not to fire on an ordinary slow round trip, short enough that nobody reads it as
// the page being broken.
const PROBE_TIMEOUT_MS = 1200;

const hasRealAvatar = (url) => {
  if (!url) return false;
  if (url.endsWith('undefined') || url.endsWith('null')) return false;
  if (url.includes('default.png')) return false;
  if (url === Api._s3_url) return false;
  return true;
};

const avatarUrl = (image) => {
  if (!image) return '';
  const url = /^https?:\/\//.test(image) ? image : `${Api._s3_url}${image}`;
  return hasRealAvatar(url) ? url : '';
};

// "10:00:00" -> { hour: 10, minute: 0, meridiem: 'AM', label: '10:00' }
const parseTime = (value) => {
  if (!value || typeof value !== 'string') return null;
  const [rawHour, rawMinute] = value.split(':');
  const hour = Number(rawHour);
  const minute = Number(rawMinute);
  if (Number.isNaN(hour) || Number.isNaN(minute)) return null;
  const meridiem = hour >= 12 ? 'PM' : 'AM';
  const hour12 = hour % 12 === 0 ? 12 : hour % 12;
  return { meridiem, label: `${hour12}:${minute.toString().padStart(2, '0')}` };
};

const formatTimeRange = (startTime, endTime) => {
  const start = parseTime(startTime);
  const end = parseTime(endTime);
  if (!start && !end) return '';
  if (!end) return `${start.label} ${start.meridiem}`;
  if (!start) return `${end.label} ${end.meridiem}`;
  return start.meridiem === end.meridiem
    ? `${start.label} – ${end.label} ${end.meridiem}`
    : `${start.label} ${start.meridiem} – ${end.label} ${end.meridiem}`;
};

const standardsLabel = (standards) => {
  if (!Array.isArray(standards) || !standards.length) return '';
  return standards
    .map((standard) => `${standard?.std ?? ''} ${standard?.section ?? ''}`.trim())
    .filter(Boolean)
    .join(', ');
};

/**
 * Route wrapper for /broadcast.
 *
 * Join now on the class card already fetches the class through joinClass() and lands here.
 * This shows the pre-join screen first and only mounts <Broadcast/> — which is what creates
 * the mediasoup room and starts producing — once Start class is pressed. No transport file,
 * redux action or endpoint changed to make that happen.
 */
const BroadcastGate = (props) => {
  const { broadcastSelectedClass, activeClass, broadcastStudentsList, sessionuser, streamId } = props;
  const isStudent = !!sessionuser?.access?.includes('schedule-student');
  const [started, setStarted] = useState(isStudent && !GATE_STUDENTS);
  // True once End class has brought us back here, which is the only thing that separates
  // this from the screen the teacher saw on the way in: the class is live-able again, so
  // the primary action reads "Re-join Class".
  const [endedEarly, setEndedEarly] = useState(false);

  // End class, from inside the live classroom. Three outcomes. Someone else's class is still
  // running, so there is nothing here to end and nothing to come back to — leave the route.
  // Otherwise it is decided by the clock against the class's own end time: inside the
  // scheduled slot the class can still be resumed, so this screen comes back with a re-join
  // action; past it there is nothing to return to and the teacher goes to the schedule home
  // page. Same comparison onGoingClassCard uses for its own isClassOver.
  //
  // `context` is what <Broadcast/> saw on the wire at the moment the button was pressed —
  // see exitClass in index.js. This screen cannot work it out itself: its lobby socket, and
  // with it the heartbeats othersLive is built from, is torn down while the class is mounted.
  const handleEndClass = (context) => {
    // A student pressing Leave is not ending anything, and dropping them on a lobby they
    // would have to leave a second time is worse than where they came from. Unchanged.
    if (isStudent) {
      props.history.goBack();
      return;
    }
    // Another teacher is still publishing. Ending the class ended nothing — this teacher was
    // watching it, not running it — so the pre-join screen would come back with every
    // control withdrawn and one button on it, and that button walks straight back into the
    // room they just left. A lobby whose only offer is to undo the thing you just did is not
    // a screen, it is a loop; leaving means leaving. The auto-join effect below refuses the
    // same loop from the other direction.
    if (context?.othersLive) {
      props.history.goBack();
      return;
    }
    const endTime = broadcastSelectedClass?.endTime;
    const classOver = endTime
      ? moment().isAfter(
          moment(`${moment().format('YYYY-MM-DD')} ${endTime}`, 'YYYY-MM-DD HH:mm:ss')
        )
      : false;
    if (classOver) {
      props.history.push('/schedule');
      return;
    }
    setEndedEarly(true);
    setStarted(false);
  };
  // id -> the room's own record for that user (statusToShow, role, name, image)
  const [lobbyUsers, setLobbyUsers] = useState(null);
  // The lobby's ChatClient is a full client, not a presence probe, so the panel can chat
  // on it. Panel state lives here rather than in PreJoin because this is what owns that
  // client; PreJoin only passes it through.
  const chatClientRef = useRef(null);
  const [chatReady, setChatReady] = useState(false);
  const [panelTab, setPanelTab] = useState('participants');
  const [chatMode, setChatMode] = useState('group');
  const [activeThread, setActiveThread] = useState(null);
  // Open/closed lives here rather than in PreJoin because what is on screen decides
  // whether an arriving message is announced - see receiveChat below.
  const [panelOpen, setPanelOpen] = useState(false);
  const startPrefsRef = useRef({ micOn: true, camOn: true, recording: false });

  // Leaving the class. join-class wrote a broadcast_user_states row for this user and this
  // room, and refuses the next join while it stands; the DELETE below is the only thing that
  // clears it. This lives on the route wrapper rather than inside <Broadcast/> on purpose —
  // the row is created by joinClass() back on the class card, before this screen mounts, so a
  // teacher who reads the pre-join screen and backs out never mounts <Broadcast/> at all and
  // would otherwise stay "joined" to a class they never entered. Every way out of /broadcast
  // — Leave, a route change, a closed tab — unmounts this component.
  const occuranceIdRef = useRef(null);
  const liveOccuranceId = activeClass?.occurance?.id;
  if (liveOccuranceId !== undefined && liveOccuranceId !== null) {
    // Held in a ref because the unmount cleanup runs after redux has often already been
    // cleared, and by then there is no id left to name the room being left.
    occuranceIdRef.current = liveOccuranceId;
  }
  const leftRef = useRef(false);
  const leaveClassroom = props.leaveBroadcast;
  useEffect(() => {
    const leave = (beacon) => {
      if (leftRef.current) return;
      leftRef.current = true;
      leaveClassroom(occuranceIdRef.current, { beacon });
    };
    // Closing the tab reaches one or the other depending on the browser, so both are
    // registered and the ref makes whichever arrives second a no-op. A live socket keeps this
    // page out of the bfcache, so pagehide here always means a real teardown.
    const onPageHide = () => leave(true);
    const onBeforeUnload = () => leave(true);
    window.addEventListener('pagehide', onPageHide);
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => {
      window.removeEventListener('pagehide', onPageHide);
      window.removeEventListener('beforeunload', onBeforeUnload);
      // Navigating away inside the SPA: the document survives, so this is an ordinary
      // request and does not need the keepalive transport.
      leave(false);
    };
  }, [leaveClassroom]);
  // Mode of recording is answered here, in the same RecordingModal the in-class Record
  // button opens, so the teacher is not asked again mid-class. Nothing records yet - there
  // is no room and no producer until Start class; this only carries the answer forward.
  const [recordModalOpen, setRecordModalOpen] = useState(false);
  const [recordPrefs, setRecordPrefs] = useState(null);
  // RecordingModal writes both of these; they only matter for an Online recording.
  const [selectedSection, setSelectedSection] = useState({});
  const [recordingBatches, setRecordingBatches] = useState([]);

  const onRecordingToggle = (next) => {
    if (!next) {
      setRecordPrefs(null);
      return;
    }
    setRecordModalOpen(true);
  };

  const [isStorageModalVisible, setStorageModalVisible] = useState(false);
  const [storageAvailableBytes, setStorageAvailableBytes] = useState(0);

  const isOutOfStorage = (details) => {
    if (details?.isSubscriptionExpired) return true;
    const available = details?.spaceAvailable;
    return available != null && !isNaN(Number(available)) && Number(available) <= 0;
  };
  const availableBytesFor = (details) => {
    if (details?.isSubscriptionExpired) return 0;
    const available = Number(details?.spaceAvailable);
    return Number.isFinite(available) && available > 0 ? available : 0;
  };

  const onRecordingConfirm = (fileName, isOffline = true) => {
    const savePrefs = () => {
      setRecordPrefs({
        fileName: fileName ?? '',
        isOffline,
        section: selectedSection,
        batches: recordingBatches,
      });
      setRecordModalOpen(false);
    };

    if (!isOffline) {
      props.getStorageDetails(null, (details) => {
        if (details && isOutOfStorage(details)) {
          setStorageAvailableBytes(availableBytesFor(details));
          setStorageModalVisible(true);
          setRecordModalOpen(false); // Close recording modal
          return;
        }
        savePrefs();
      });
      return;
    }
    savePrefs();
  };

  const recordingNote = recordPrefs
    ? [recordPrefs.isOffline ? 'Offline' : 'Online', recordPrefs.fileName?.trim()]
        .filter(Boolean)
        .join(' · ')
    : '';

  // Incoming chat, running the same branches broadcast/index.js runs for its own client:
  // a message of mine is persisted with postChat, anyone else's bumps the unread badge,
  // and either way it lands in the redux slice <Broadcast/> reads - so what was said in
  // the lobby is still on screen after Start class swaps in a fresh client.
  //
  // Held in a ref and reassigned each render: the socket effect below must not list this
  // in its deps, or every arriving message would tear the room down and rejoin it.
  // userId -> when their last publish heartbeat arrived. Three heartbeat periods of silence
  // and they are treated as gone; see the 'Event' branch below.
  const [publisherSeenAt, setPublisherSeenAt] = useState({});
  const [staleTick, setStaleTick] = useState(0);
  // The heartbeats themselves re-render this, but the last publisher stopping produces no
  // message at all - so without a tick of its own the flag would sit true until some
  // unrelated render happened to recompute it.
  useEffect(() => {
    if (!Object.keys(publisherSeenAt).length) return undefined;
    const id = setInterval(() => setStaleTick((was) => was + 1), PUBLISHER_STALE_MS / 3);
    return () => clearInterval(id);
  }, [publisherSeenAt]);
  const othersLive = useMemo(() => {
    const now = Date.now();
    return Object.values(publisherSeenAt).some((pub) => {
      const at = typeof pub === 'number' ? pub : pub.at;
      return now - at <= PUBLISHER_STALE_MS;
    });
    // staleTick is in the deps for its side effect alone - it is what re-evaluates this as
    // entries age out, and nothing in the body reads it.
  }, [publisherSeenAt, staleTick]);


  // The connection, while this screen is the one on display.
  //
  // Everything the pre-join screen is waiting on arrives over the network — the roster, the
  // chat, the heartbeat that decides whether a class is already running — and none of it
  // announces its own absence. Offline, the screen simply sits there: an empty roster and a
  // Start class button that leads into a room that cannot be built. The browser knows, and
  // is the only thing here that does, so it is what gets asked.
  //
  // Both edges are announced. Coming back matters as much as going: a teacher who has been
  // told to check their connection has no other way of learning that it is worth trying
  // again, short of pressing the button to find out.
  const wasOfflineRef = useRef(false);
  useEffect(() => {
    if (started) return undefined;
    const announceOffline = () => {
      if (wasOfflineRef.current) return;
      wasOfflineRef.current = true;
      Notification.error('Error', 'You are offline. The class cannot start until your connection is back.');
    };
    const announceOnline = () => {
      if (!wasOfflineRef.current) return;
      wasOfflineRef.current = false;
      Notification.success('success', 'You are back online.');
    };
    if (navigator.onLine === false) announceOffline();
    window.addEventListener('offline', announceOffline);
    window.addEventListener('online', announceOnline);
    return () => {
      window.removeEventListener('offline', announceOffline);
      window.removeEventListener('online', announceOnline);
    };
  }, [started]);

  // Who has answered the probe, by id — see the Event branch of receiveChatRef.
  const [answered, setAnswered] = useState({});

  // Every other non-student the room reports as online: the complete set of people who could
  // be publishing, and therefore the set the probe is waiting to hear from. Students never
  // publish and never answer, so counting them would make the wait permanent.
  const staffOnline = useMemo(() => {
    if (!lobbyUsers) return null;
    const selfId = `${sessionuser?.id ?? ''}`;
    const ids = [];
    lobbyUsers.forEach((item, id) => {
      if (id === selfId || item?.statusToShow !== 'online') return;
      if (roleOf({ role: item.role, privilege: item.privilege }) !== 'Student') ids.push(`${id}`);
    });
    return ids;
  }, [lobbyUsers, sessionuser?.id]);

  // Whether this screen still has an open question.
  //
  // What it is waiting on is narrow: is somebody already publishing, in which case this
  // teacher is walked straight into their class and the pre-join screen would only flash
  // past on the way. The old answer was to wait one heartbeat period for everyone, and that
  // is a wait charged overwhelmingly to the teacher who is about to start the class and has
  // nothing to wait for. The probe replaces the waiting with asking: the room is asked on
  // connect and everyone who could publish answers at once, so the question closes in a
  // round trip rather than in a heartbeat period.
  //
  // Three ways to close it, and only the last is a wait:
  //   - a publisher answered, so we are going in;
  //   - everyone who could publish has answered and none of them is, so we are not;
  //   - nobody answered in time. PROBE_TIMEOUT_MS is the backstop for a room whose other
  //     teacher is wedged or on an older build that does not know how to reply.
  const settled = useMemo(() => {
    if (isStudent || started || endedEarly) return true;
    if (othersLive) return true;
    if (!staffOnline) return false;
    return staffOnline.every((id) => answered[id]);
  }, [isStudent, started, endedEarly, othersLive, staffOnline, answered]);
  const [probeTimedOut, setProbeTimedOut] = useState(false);
  useEffect(() => {
    if (settled || probeTimedOut) return undefined;
    const id = setTimeout(() => setProbeTimedOut(true), PROBE_TIMEOUT_MS);
    return () => clearTimeout(id);
  }, [settled, probeTimedOut]);
  // The answer came back "somebody is publishing", and the effect below is about to walk
  // this teacher into that class — but it cannot, until after this render has been painted.
  // That one frame is the flash: the pre-join screen appearing and being torn away again,
  // and worse than seen, its mount reaches for the camera to build a preview that is
  // discarded a moment later.
  //
  // So the decision is read here as well as acted on below, and the two conditions have to
  // stay identical or this holds a blank screen open waiting for a join that is never going
  // to happen. Offline included: that is the one case where the effect declines, and the
  // teacher belongs on the pre-join screen, which is where the connection is reported.
  const autoJoinPending = !started && !isStudent && !endedEarly && othersLive
    && navigator.onLine !== false;
  const probing = (!settled && !probeTimedOut) || autoJoinPending;

  // Auto-join, for the teacher who arrives while a class is already running. Everything this
  // screen offers - mic, camera, recording - belongs to whoever holds the room's single
  // publisher slot, and someone else holds it; with all of it withdrawn the only control
  // left that does anything is Start class, so press it for them. The class itself gives the
  // buttons back the moment that publish stops, which is where those choices can be honoured.
  //
  // Not for students - waiting is what their lobby is for - and not after End class:
  // endedEarly means this teacher has just left a room the other teacher is still publishing
  // in, and sending them straight back is a loop with no way out of it. Not offline either:
  // the heartbeat that set othersLive may be seconds old and the room is unreachable now, so


  useEffect(() => {
    if (started || isStudent || endedEarly || !othersLive) return;
    if (navigator.onLine === false) return;

    // Block non-assigned teachers from joining if a teacher is already sharing
    const assignedTeacherId = broadcastSelectedClass?.teacher?.id || activeClass?.occurance?.teacherId;
    if (sessionuser?.id !== assignedTeacherId) {
      Notification.error("Error", "A teacher is already taking the class");
      props.history.replace('/schedule');
      return;
    }

    // Nothing of this teacher's own goes on the wire: the room would refuse the publish, and
    // startPrefs would still be asking for it. Same prefs PreJoin sends on its othersLive
    // path, plus the one thing the class cannot work out for itself — that nobody pressed
    // anything to get here, which is what the notice on the other side is written from.
    startPrefsRef.current = { micOn: false, camOn: false, recording: false, autoJoined: true };
    setStarted(true);
  }, [started, isStudent, endedEarly, othersLive, sessionuser, broadcastSelectedClass, activeClass, props.history]);

  // Everything the lobby needs to know about what the reader can already see. A message
  // that lands in the conversation on screen announces nothing - the class does the same,
  // and a toast over the very line that just appeared is only noise.
  const groupChatOnScreen = panelOpen && panelTab === 'chat' && chatMode === 'group';
  const threadOnScreen = (id) =>
    panelOpen && panelTab === 'chat' && chatMode === 'private' && `${activeThread?.id ?? ''}` === `${id}`;
  // Every private thread together, which is what the Private pill and the closed panel
  // have room to say.
  const privateUnreadTotal = useMemo(
    () => Object.values(props.unreadCount?.private ?? {}).reduce((sum, count) => sum + (count || 0), 0),
    [props.unreadCount],
  );

  const receiveChatRef = useRef(null);
  receiveChatRef.current = (event) => {
    const payload = event?.data;
    if (!payload) return;
    // 'Event' is the publish heartbeat every non-student in the live room sends every 2s.
    // This screen does act on it: the room holds one publisher, and mic, camera and Record
    // here are all choices about going live. Hearing someone else's state:true means a class
    // is already running and none of those choices are available yet.
    if (payload.type === 'Event') {
      let beat = payload.data;
      if (typeof beat === 'string') {
        try { beat = JSON.parse(beat) } catch (error) { return }
      }
      let who = beat?.user;
      if (typeof who === 'string') {
        try { who = JSON.parse(who) } catch (error) { return }
      }
      if (!who?.id || `${who.id}` === `${sessionuser?.id}`) return;
      if (who.access?.includes('schedule-student')) return;
      // Somebody else has just arrived and is asking the room what it is doing. Answer, even
      // though this screen publishes nothing: an unanswered question is indistinguishable
      // from a slow one, and the point of the probe is that the asker can stop waiting as
      // soon as everyone who could be publishing has spoken. A false here is what lets them.
      if (beat.probe) {
        chatClientRef.current?.sendGroupChat(sessionuser.id, streamId, {
          type: 'Event',
          data: { state: false, user: sessionuser },
        });
        return;
      }
      // Who has spoken, separately from what they said. othersLive only tracks publishers,
      // so on its own it cannot tell "nobody is live" from "nobody has answered yet" — and
      // that difference is the whole of the wait this screen used to impose on everyone.
      setAnswered((previous) => (previous[who.id] ? previous : { ...previous, [who.id]: true }));
      // Stored as "last heard at" rather than a flag. There is no message for leaving, so a
      // teacher who closes their tab simply stops sending - and a timestamp that stops being
      // refreshed expires by itself, where a flag would stay stuck on.
      setPublisherSeenAt((previous) => {
        if (beat?.state) return { ...previous, [who.id]: { at: Date.now(), user: who } };
        if (!(who.id in previous)) return previous;
        const next = { ...previous };
        delete next[who.id];
        return next;
      });
      return;
    }
    if (typeof payload.data === 'string') {
      try {
        payload.data = JSON.parse(payload.data);
      } catch (error) {
        return;
      }
    }
    const from = payload.data?.user;
    if (!from) return;
    const mine = `${from.id}` === `${sessionuser?.id}`;
    const message =
      payload.type === 'Image'
        ? { id: uuidv4(), type: 'Image', image: payload.data.image, createdAt: new Date(), user: from }
        : {
            id: uuidv4(),
            type: payload.type,
            userId: payload.userId,
            user: from,
            text: payload.data.text,
            createdAt: payload.data.createdAt,
          };

    if (event.event === 'rcvChat') {
      if (mine) {
        if (payload.type !== 'Image') props.postChat(message, 'Group');
      } else {
        props.updateUnreadCount('groupIncrement');
        if (!groupChatOnScreen) MessageNotification.group();
      }
      props.addMessageToGroup(message);
      return;
    }

    if (event.event === 'privateChatRcv') {
      // The thread is keyed by the other person, which is the recipient on a message I
      // sent and the sender on one I received.
      const other = mine ? `${event.userId}` : `${from.id}`;
      if (mine) {
        if (payload.type !== 'Image') props.postChat({ ...message, reciever: other }, 'Private');
      } else {
        props.updateUnreadCount('privateIncrement', undefined, from.id);
        if (!threadOnScreen(from.id)) MessageNotification.private(from.name);
      }
      props.addMessageToPrivate(other, message);
    }
  };

  // Lobby presence. A bare subscribe is not enough: the server publishes the room's
  // userList in reply to `initSockets`, which is what ChatClient's constructor sends. With
  // only a subscription the list arrives on the next join or leave, so anyone already
  // waiting stayed invisible until the class started — which is the bug this fixes.
  //
  // The cost is that this teacher is announced to the room while still on this screen,
  // the same as before the redesign, when Join now went straight into /broadcast. Getting
  // the roster without announcing would need the server to answer a read-only request.
  useEffect(() => {
    if (started || !streamId || sessionuser?.id === undefined) return undefined;
    let socket = null;
    let cancelled = false;
    try {
      socket = setUpChatSocket();
      chatClientRef.current = new ChatClient(
        socket,
        sessionuser.id,
        streamId,
        sessionuser.name,
        sessionuser.privilage?.join(','),
        sessionuser.role?.join(','),
        sessionuser.image,
        activeClass?.occurance?.id,
        streamId?.split('xxxx')[0],
        sessionuser.org?.url,
        (event) => {
          if (cancelled) return;
          if (event?.event === 'userList') {
            if (!Array.isArray(event.data)) return;
            const next = new Map();
            event.data.forEach((item) => {
              if (item?.userId === undefined) return;
              next.set(`${item.userId}`, item);
            });
            setLobbyUsers(next);
            return;
          }
          receiveChatRef.current?.(event);
        },
      );
      setChatReady(true);
      // Ask the room what it is doing, rather than waiting to overhear it. Every non-student
      // in here answers with their publish state the moment this lands — the live class in
      // index.js from its own Event branch, another lobby from the probe branch above — so
      // the question this screen opens with is closed in one round trip instead of in a
      // heartbeat period. Sent once, on connect: the 2s heartbeats take over from here, and
      // a probe that goes missing is covered by PROBE_TIMEOUT_MS rather than by asking again.
      //
      // Not from a student. They are never auto-joined, so they hold nothing back waiting
      // for the answer — and the live class filters students out of this branch anyway, so
      // the question would go unanswered.
      if (!isStudent) {
        chatClientRef.current?.sendGroupChat(sessionuser.id, streamId, {
          type: 'Event',
          data: { probe: true, user: sessionuser },
        });
      }
    } catch (error) {
      // presence is best effort — the screen works without it
    }
    return () => {
      cancelled = true;
      chatClientRef.current = null;
      setChatReady(false);
      try {
        // Dropping the socket takes the ChatClient's listeners with it. <Broadcast/> opens
        // its own client on Start class, and two on one room would double every message.
        socket?.disconnect?.();
      } catch (error) {
        // nothing to clean up
      }
    };
  }, [started, streamId, sessionuser?.id, activeClass?.occurance?.id]);

  // The class roster, split the way the panel shows it: who the room reports as online
  // ("Present") and everyone else on the roster ("Absent").
  const { present, absent, roster } = useMemo(() => {
    const source =
      (Array.isArray(activeClass?.students) && activeClass.students.length && activeClass.students) ||
      (Array.isArray(broadcastStudentsList) && broadcastStudentsList) ||
      [];
    const selfId = `${sessionuser?.id ?? ''}`;
    const byId = new Map();

    source.forEach((item) => {
      if (!item) return;
      const id = `${item.id ?? item.userId ?? ''}`;
      if (!id || id === selfId) return;
      byId.set(id, {
        id,
        name: item.name ?? item.userName ?? 'Student',
        image: avatarUrl(item.image),
        role: roleOf(item),
        online: false,
        muted: false,
      });
    });

    // Anyone the room reports who isn't on the roster (a co-host joining, say) still shows.
    lobbyUsers?.forEach((item, id) => {
      if (id === selfId) return;
      const existing = byId.get(id);
      const merged = {
        id,
        name: existing?.name ?? item.userName ?? 'Participant',
        image: existing?.image ?? avatarUrl(item.image),
        role: existing?.role ?? roleOf({ role: item.role, privilege: item.privilege }),
        online: item.statusToShow === 'online',
        muted: !!existing?.muted,
      };
      byId.set(id, merged);
    });

    const all = [...byId.values()];
    // Everyone, not students only — same rule as the in-class panel in index.js. roleOf is
    // what draws the label under each name, so a co-host, an admin sitting in or a second
    // teacher reads as what they are rather than having to be hidden to avoid being
    // mislabelled. Only this teacher's own row is left out: this is the lobby, and
    // `present` is also what "N waiting" counts, which is other people.
    return {
      roster: all,
      present: all.filter((person) => person.online),
      absent: all.filter((person) => !person.online),
    };
  }, [activeClass, broadcastStudentsList, lobbyUsers, sessionuser]);

  const amIFirstTeacher = useRef(false);

  // Block if another teacher/superadmin is actively shown as 'present' on the pre-join page
  useEffect(() => {
    if (started || isStudent || !lobbyUsers || !sessionuser?.id) return;
    if (amIFirstTeacher.current) return; // We were here first, don't kick us out

    const otherTeacherOrAdmin = present.find(
      (u) => `${u.id}` !== `${sessionuser?.id}` && (
        u.role === 'Teacher' || u.role === 'Super Admin' || u.role === 'Admin'
      )
    );

    if (otherTeacherOrAdmin) {
      Notification.error("Error", "Another teacher or superadmin is already taking the class.");
      props.history.replace('/schedule');
    } else {
      // If we received lobbyUsers and no other teacher is here, we are the first!
      amIFirstTeacher.current = true;
    }
  }, [present, lobbyUsers, started, isStudent, sessionuser, props.history]);

  const chatUser = useMemo(
    () => ({
      id: sessionuser?.id,
      name: sessionuser?.name,
      avatar: `${Api._s3_url}${sessionuser?.image}`,
    }),
    [sessionuser],
  );

  // Private conversations, built from the history joinClass() already put in redux and
  // named off the roster above. Numeric keys only: the in-class reducer also seeds this
  // map with a user object, which stringifies to "[object Object]".
  const chatThreads = useMemo(() => {
    const byId = new Map(roster.map((person) => [person.id, person]));
    const allIds = new Set([
      ...Object.keys(props.privateMessages ?? {}),
      ...Array.from(byId.keys()),
    ]);
    return Array.from(allIds)
      .filter((id) => /^\d+$/.test(id) && id !== `${sessionuser?.id}`)
      .map((id) => {
        const thread = props.privateMessages?.[id] ?? [];
        const last = thread[thread.length - 1];
        const info = byId.get(id);
        const user = info ?? last?.user;
        if (!user) return null;
        return {
          id,
          name: user.name ?? 'Participant',
          image: user.image ?? '',
          // Unknown presence stays undefined so the row draws no dot; see the same note in
          // broadcast/index.js.
          online: info ? !!info.online : undefined,
          role: info?.role ?? 'Student',
          lastMessage: last?.type === 'Image' ? 'Photo' : last?.text,
          lastMessageTime: last?.createdAt,
          unread: props.unreadCount?.private?.[id] ?? 0,
          raw: info,
        };
      }).filter(Boolean);
  }, [props.privateMessages, props.unreadCount, roster, sessionuser]);

  const markGroupRead = () => {
    props.updateUnreadCount('group', 0);
    props.postMessageOpenStatus({ type: 'Group', roomName: streamId?.split('xxxx')[1] });
  };

  const markThreadRead = (id) => {
    props.updateUnreadCount('privateSpecific', 0, id);
    // update-chat-read validates senderId as a number and rejects the call otherwise;
    // every id on this screen is a string, so convert rather than forward it.
    const senderId = Number(id);
    if (!Number.isFinite(senderId)) return;
    props.postMessageOpenStatus({
      type: 'Private',
      roomName: streamId?.split('xxxx')[1],
      senderId,
    });
  };

  // Who an upload is addressed to. postChatImage takes the recipient off redux rather than
  // from its arguments, so opening a thread has to publish it there as well as into the
  // local view state. Ids are strings on this screen and the endpoint wants a number.
  const rememberRecipient = (person) => {
    const numericId = Number(person.id);
    props.setCurrentPrivateChatReciever({
      ...person,
      id: Number.isFinite(numericId) ? numericId : person.id,
    });
  };

  // What the row's chat button does: open that person's thread in the Chat tab. Students
  // may message the teacher and co-hosts but not each other - the same rule the in-class
  // panel enforces.
  const openThread = (person) => {
    if (!person?.id) return;
    if (isStudent && person.role === 'Student') return;
    setActiveThread({ id: `${person.id}`, name: person.name });
    rememberRecipient(person);
    setChatMode('private');
    setPanelTab('chat');
    markThreadRead(`${person.id}`);
  };

  const sendGroupText = (text) => {
    chatClientRef.current?.sendGroupChat(sessionuser.id, streamId, {
      type: 'Text',
      data: { id: uuidv4(), text, createdAt: new Date(), user: chatUser },
      userId: sessionuser.id,
      roomId: streamId,
    });
  };

  // Same upload the class uses. /chat/upload stores the message, so there is no postChat
  // to follow it; the returned URL is what gets published to the room.
  const imageParams = (file) => ({
    file,
    userId: sessionuser.id,
    roomName: streamId?.split('xxxx')[1],
    type: 'image',
  });

  const sendGroupImage = (file) => {
    props.postChatImage(imageParams(file), chatUser, (newMessage) => {
      chatClientRef.current?.sendGroupChat(sessionuser.id, streamId, {
        type: 'Image',
        data: newMessage,
        userId: sessionuser.id,
        roomId: streamId,
      });
    }, 'Group');
  };

  const sendPrivateImage = (file) => {
    if (!activeThread?.id) return;
    props.postChatImage(imageParams(file), chatUser, (newMessage) => {
      chatClientRef.current?.sendPrivateChat(sessionuser.id, streamId, activeThread.id, {
        type: 'Image',
        data: newMessage,
        userId: sessionuser.id,
        roomId: streamId,
        user: sessionuser,
      });
    }, 'Private');
  };

  const sendPrivateText = (text) => {
    if (!activeThread?.id) return;
    chatClientRef.current?.sendPrivateChat(sessionuser.id, streamId, activeThread.id, {
      type: 'Text',
      data: { id: uuidv4(), text, createdAt: new Date(), user: chatUser },
      userId: sessionuser.id,
      roomId: streamId,
      user: sessionuser,
    });
  };

  if (started) {
    // Forward only the route props — Broadcast is connected and reads the rest itself.
    return (
      <Broadcast
        history={props.history}
        location={props.location}
        match={props.match}
        startPrefs={startPrefsRef.current}
        onEndClass={handleEndClass}
      />
    );
  }

  const subjectData = broadcastSelectedClass?.subject ?? activeClass?.occurance?.subject;
  const subject = subjectData?.name;
  const subjectIcon = subjectData?.icon ? `${Api._s3_url}${subjectData.icon}` : '';
  const subjectInitial = (subject ?? '').trim().charAt(0).toUpperCase() || 'C';
  const batch =
    standardsLabel(broadcastSelectedClass?.standards) || broadcastSelectedClass?.batchName || '';
  const title = [subject, batch].filter(Boolean).join(' · ') || 'Live class';
  // studentCount has come back as a string from the API, and `typeof` checks downstream
  // then read it as "no count yet" and showed the loading line with a full roster on
  // screen. Coerce, and fall back to what the roster itself says.
  const rawInvited = broadcastSelectedClass?.attendanceData?.studentCount;
  const parsedInvited = Number(rawInvited);
  const invitedCount = Number.isFinite(parsedInvited) && rawInvited !== null && rawInvited !== ''
    ? parsedInvited
    : (roster.length ? roster.length : undefined);

  // The probe is still out. Nothing is drawn for the round trip it takes, deliberately: this
  // is the gap between pressing Join on the schedule and the classroom appearing, and a
  // screen put in it is a screen someone has to watch come and go. The pre-join screen in
  // particular cannot go here — half the time the answer is that a class is already running
  // and this teacher is about to be walked into it, and showing them a lobby they never get
  // to use is the flash this exists to prevent. It also must not acquire a camera for a
  // preview that is about to be thrown away.
  if (probing) return null;

  return (
    <>
      <PreJoin
        rejoin={endedEarly}
        subjectInitial={subjectInitial}
        subjectIcon={subjectIcon}
        title={title}
        userName={sessionuser?.name ?? 'You'}
        timeRange={formatTimeRange(broadcastSelectedClass?.startTime, broadcastSelectedClass?.endTime)}
        invitedCount={invitedCount}
        othersLive={othersLive}
        waitingCount={lobbyUsers ? present.length : null}
        present={present}
        absent={absent}
        onMessage={openThread}
        panelOpen={panelOpen}
        onPanelOpenChange={setPanelOpen}
        chatUnread={(props.unreadCount?.group ?? 0) + privateUnreadTotal}
        panelTab={panelTab}
        onPanelTabChange={(tab) => {
          setPanelTab(tab);
          if (tab !== 'chat') return;
          // The Chat tab opens on the group, every time. Private is a deliberate choice -
          // the Private pill, or a row's message button - and it used to outlive the visit
          // it was made in: one look at a thread and the tab kept reopening on that person,
          // or on a thread list, when what the tab says is "chat" and what the class shares
          // is the group. The thread is cleared with it so Back is never the way out of a
          // conversation nobody chose to open.
          setChatMode('group');
          setActiveThread(null);
          markGroupRead();
        }}
        chatSlot={
          <ChatPanel
            mode={chatMode}
            groupUnread={props.unreadCount?.group ?? 0}
            privateUnread={privateUnreadTotal}
            onModeChange={(mode) => {
              setChatMode(mode);
              setActiveThread(null);
              if (mode === 'group') markGroupRead();
            }}
            selfId={sessionuser?.id}
            groupMessages={props.groupMessages}
            threads={chatThreads}
            activeThread={activeThread}
            threadMessages={activeThread ? props.privateMessages?.[activeThread.id] ?? [] : []}
            onOpenThread={(thread) => {
              setActiveThread({ id: `${thread.id}`, name: thread.name });
              rememberRecipient(thread.raw ?? { id: thread.id, name: thread.name });
              markThreadRead(thread.id);
            }}
            onCloseThread={() => setActiveThread(null)}
            onSendGroup={sendGroupText}
            onSendPrivate={sendPrivateText}
            onSendGroupImage={sendGroupImage}
            onSendPrivateImage={sendPrivateImage}
            canSend={chatReady}
          />
        }
        isStudent={isStudent}
        recording={!!recordPrefs}
        recordingNote={recordingNote}
        onRecordingToggle={onRecordingToggle}
        onStart={(prefs) => {
          // Offline: PreJoin sends null rather than a set of choices, having kept its own
          // devices alive for the retry. Mounting <Broadcast/> anyway would take the teacher
          // out of a screen that can say what is wrong and into one that cannot — the room
          // build fails somewhere inside the transport, the stage comes up empty, and the
          // only thing on offer is End class. Refuse while there is still something to
          // refuse from. Checked here as well because the connection can drop between the
          // two, and this is the side that does the mounting.
          if (!prefs || navigator.onLine === false) {
            Notification.error('Error', 'You are offline. Check your internet connection and start the class again.');
            return;
          }
          // The mode/name answered here travels with the other pre-join choices;
          // index.js starts the recording once the class is actually publishing.
          startPrefsRef.current = {
            ...prefs,
            recording: !!recordPrefs,
            recordFileName: recordPrefs?.fileName,
            recordIsOffline: recordPrefs?.isOffline,
            recordSection: recordPrefs?.section,
            recordBatches: recordPrefs?.batches,
          };
          setStarted(true);
        }}
        onLeave={() => props.history?.goBack()}
      />
      {recordModalOpen ? (
        <RecordingModal
          visible={recordModalOpen}
          title={'Confirm'}
          content={'Are you sure you want to record the class?'}
          isRecording={0}
          recordCount={props.recordCount}
          localRecordingCount={
            streamId && props.localRecordingName && streamId in props.localRecordingName ? 1 : 0
          }
          selectedSchedule={activeClass?.occurance ?? {}}
          sectionList={props.sectionList}
          getVideoResourceSections={props.getVideoResourceSections}
          selectedSection={selectedSection}
          setSelectedSection={setSelectedSection}
          recordingBatches={recordingBatches}
          setRecordingBatches={setRecordingBatches}
          postCreateTopic={props.postCreateTopic}
          onCancel={() => setRecordModalOpen(false)}
          onConfirm={onRecordingConfirm}
        />
      ) : null}
      {isStorageModalVisible ? (
        <AulasSubscriptionModal
          isModalVisible={isStorageModalVisible}
          handleCancel={() => setStorageModalVisible(false)}
          availableStorageBytes={storageAvailableBytes}
          isStorageVariant={true}
        />
      ) : null}
    </>
  );
};

const mapStateToProps = (state) => {
  const { streamId, sectionList, localRecordingName, groupMessages, privateMessages, unreadCount } =
    state.broadcast;
  const { user } = state.session;
  const { orgData } = state.loginNew;
  const { broadcastSelectedClass, broadcastStudentsList, activeClass, recordCount } =
    state.schedule;
  return {
    streamId,
    orgData,
    sessionuser: user,
    broadcastSelectedClass,
    broadcastStudentsList,
    activeClass,
    sectionList,
    localRecordingName,
    recordCount,
    groupMessages,
    privateMessages,
    unreadCount,
  };
};

// The same actions broadcast/index.js already dispatches: what RecordingModal calls (the
// section list for an Online recording, topic creation), what the lobby chat panel calls,
// and the leave that clears this user's broadcast_user_states row on the way out.
const mapDispatchToProps = (dispatch) => ({
  getVideoResourceSections: (id, callback) => dispatch(getVideoResourceSections(id, callback)),
  postCreateTopic: (object, callback, successCallback) =>
    dispatch(postCreateTopic(object, callback, successCallback)),
  addMessageToGroup: (message) => dispatch(addMessageToGroup(message)),
  addMessageToPrivate: (reciever, message) => dispatch(addMessageToPrivate(reciever, message)),
  postChat: (message, chatType) => dispatch(postChat(message, chatType)),
  postChatImage: (params, user, callback, type) =>
    dispatch(postChatImage(params, user, callback, type)),
  setCurrentPrivateChatReciever: (user) => dispatch(setCurrentPrivateChatReciever(user)),
  updateUnreadCount: (key, value, id) => dispatch(updateUnreadCount(key, value, id)),
  postMessageOpenStatus: (params) => dispatch(postMessageOpenStatus(params)),
  leaveBroadcast: (occuranceId, options) => dispatch(leaveBroadcast(occuranceId, options)),
  getStorageDetails: (callback, successCallback) => dispatch(getStorageDetails(callback, successCallback)),
});

export default connect(mapStateToProps, mapDispatchToProps)(BroadcastGate);
